import { useEffect, useState, useCallback, useMemo } from 'react'
import './UpcomingEvents.css'
import BidCard from './BidCard'
import { Link } from 'react-router-dom'
import { CiSearch } from "react-icons/ci";
import UpcomingEventsCarousel from './UpcomingEventsCarousel';
import axios from 'axios';
import BidCardSkeleton from './BidCardSkeleton';
import { API_BASE_URL } from '../../utils';
import UpcomingPlanModal from './UpcomingPlanModal';
import noUpcomingEvent from '../image/no-upcoming-events.svg'
import loginBanner from '../image/loginPromotion.jpg'
import { FaChevronUp, FaChevronDown } from "react-icons/fa";

export default function UpcomingEvents({ setModalOpen, setSelecteLead }) {
  const userdata = JSON.parse(localStorage.getItem("userData"));

  // State management
  const [packageData, setPackageData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [location, setLocation] = useState({ latitude: null, longitude: null });
  const [activeTab, setActiveTab] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isOpenBox, setIsOpenBox] = useState(false);
  const [loading, setLoading] = useState(false);

  // Memoized computed values
  const packageLeads = useMemo(() =>
    packageData.map(({ bids, ...rest }) => rest),
    [packageData]
  );

  const packageBids = useMemo(() => {
    if (!activeIndex || activeTab !== "package") {
      return packageData.flatMap(item => item.bids || []);
    }
    const matched = packageData.find(item => item.id === activeIndex);
    return matched?.bids || [];
  }, [packageData, activeIndex, activeTab]);

  const tableLeads = useMemo(() =>
    tableData.map(({ bids, ...rest }) => rest),
    [tableData]
  );

  const tableBids = useMemo(() => {
    if (!activeIndex || activeTab !== "table") {
      return tableData.flatMap(item => item.bids || []);
    }
    const matched = tableData.find(item => item.id === activeIndex);
    return matched?.bids || [];
  }, [tableData, activeIndex, activeTab]);

  // Determine which tab to show based on available data
  const determineDefaultTab = useCallback(() => {
    if (tableData.length > 0 && packageData.length > 0) {
      return "table"; // Show table first if both have data
    } else if (tableData.length > 0) {
      return "table";
    } else if (packageData.length > 0) {
      return "package";
    }
    return "table"; // Default fallback
  }, [tableData.length, packageData.length]);

  // API calls
  const fetchPackageEvents = useCallback(async (lat, lng, userId) => {
    try {
      const formData = new FormData();
      formData.append('latitude', lat);
      formData.append('longitude', lng);
      formData.append('user_id', userId);

      const response = await axios.post(
        `${API_BASE_URL}/master/APIs/ClubPackage/getGroupBookingUserBids`,
        formData
      );

      if (response.data.status === true) {
        setPackageData(response.data.data || []);
      } else {
        console.warn('No package events found:', response.data.msg);
        setPackageData([]);
      }
    } catch (error) {
      console.error('Error fetching package events:', error);
      setPackageData([]);
    }
  }, []);

  const fetchTableEvents = useCallback(async (lat, lng, userId) => {
    try {
      const formData = new FormData();
      formData.append('latitude', lat);
      formData.append('longitude', lng);
      formData.append('user_id', userId);

      const response = await axios.post(
        `${API_BASE_URL}/master/APIs/ClubTable/getUserBids`,
        formData
      );

      if (response.data.status === true) {
        setTableData(response.data.data || []);
      } else {
        console.warn('No table events found:', response.data.msg);
        setTableData([]);
      }
    } catch (error) {
      console.error('Error fetching table events:', error);
      setTableData([]);
    }
  }, []);

  // Get user location and fetch data
  useEffect(() => {
    if (!userdata?.id) return;

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const latStr = latitude.toString();
          const lngStr = longitude.toString();

          setLocation({ latitude: latStr, longitude: lngStr });

          try {
            setLoading(true);
            await Promise.all([
              fetchPackageEvents(latStr, lngStr, userdata.id),
              fetchTableEvents(latStr, lngStr, userdata.id)
            ]);
          } finally {
            setLoading(false);
          }
        },
        (error) => {
          console.error('Error in finding location:', error);
          alert('Please enable Geo Location Service to find events in your locality.');
          setLoading(false);
        }
      );
    } else {
      console.error('Geolocation is not available in this browser.');
      setLoading(false);
    }
  }, [userdata?.id, fetchPackageEvents, fetchTableEvents]);

  // Set default tab based on available data
  useEffect(() => {
    if (!loading && !activeTab) {
      const defaultTab = determineDefaultTab();
      setActiveTab(defaultTab);
    }
  }, [loading, activeTab, determineDefaultTab]);

  // Update selected lead when tab or index changes
  useEffect(() => {
    if (!activeTab || !activeIndex || !setSelecteLead) return;

    const leads = activeTab === "package" ? packageLeads : tableLeads;
    const selectedLead = leads.find(lead => lead?.id === activeIndex);

    if (selectedLead) {
      setSelecteLead(selectedLead);
    }
  }, [activeTab, activeIndex, packageLeads, tableLeads, setSelecteLead]);

  // Current data based on active tab
  const currentLeads = activeTab === "package" ? packageLeads : tableLeads;
  const currentBids = activeTab === "package" ? packageBids : tableBids;
  const hasAnyData = packageData.length > 0 || tableData.length > 0;

  // Tab change handler
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setActiveIndex(0); // Reset active index when switching tabs
  }, []);

  // Render loading skeleton
  const renderBidSkeleton = (count) => (
    Array.from({ length: count }).map((_, index) => <BidCardSkeleton key={index} />)
  );

  // Render bid cards
  const renderBidCards = (bids, isTable, count) => {
    if (bids.length === 0) {
      return renderBidSkeleton(count);
    }

    return bids.slice(0, count).map((bid, index) => (
      <BidCard key={`${bid.id || index}`} data={bid} istableleads={isTable} />
    ));
  };

  if (loading) {
    return (
      <>
        {/* Desktop Loading */}
        <div className='upcoming-events-container-web'>
          <div className='upcoming-events'>
            <div className='upcoming-events-header'>
              <Link to='/package-lead-form'>
                <div className='upcoming-events-empty'>
                  <img src={noUpcomingEvent} alt="No upcoming events" />
                </div>
              </Link>
            </div>
            {renderBidSkeleton(3)}
          </div>
        </div>

        {/* Mobile Loading */}
        <div className='upcoming-events-container-mobile'>
          <div className='upcoming-events'>
            <div className='upcoming-events-header'>
              <h3>Loading...</h3>
            </div>
            <div className='upcoming-events-header-bids-mobile'>
              {renderBidSkeleton(1)}
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!hasAnyData) {
    return (
      <>
        <div className='upcoming-events-container-web'>
          {userdata?.id ? (
            <Link to='/package-lead-form'>
              <div className='upcoming-events-empty'>
                <img src={noUpcomingEvent} alt="No upcoming events" />
              </div>
            </Link>
          ) : (
            <Link to='/login'>
              <div className='upcoming-events-empty'>
                <img src={loginBanner} alt="No upcoming events" />
              </div>
            </Link>
          )}
        </div>

        <div className='upcoming-events-container-mobile'>
          <Link to='/package-lead-form'>
            <h1 style={{ textAlign: "center", fontSize: "14px", textDecoration: "underline" }}>
              Plan Your Party Now
            </h1>
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Desktop View */}
      <div className='upcoming-events-container-web'>
        <div className='upcoming-events'>
          <div className='upcoming-events-header'>
            <h3>Upcoming Plan</h3>
          </div>

          {/* Tabs */}
          <div className='upcoming-events-header-tabs'>
            <div
              className={activeTab === "table" ?
                "upcoming-events-header-tabs-box-active-tab" :
                "upcoming-events-header-tabs-box"
              }
              onClick={() => handleTabChange("table")}
              style={{ opacity: tableData.length === 0 ? 0.5 : 1 }}
            >
              {/* Table Booking {tableData.length > 0 && `(${tableLeads.length})`} */}
              Table Booking
            </div>
            <div
              className={activeTab === "package" ?
                "upcoming-events-header-tabs-box-active-tab" :
                "upcoming-events-header-tabs-box"
              }
              onClick={() => handleTabChange("package")}
              style={{ opacity: packageData.length === 0 ? 0.5 : 1 }}
            >
              {/* Package Booking {packageData.length > 0 && `(${packageLeads.length})`} */}
              Package Booking
            </div>
          </div>

          {/* Content based on active tab */}
          {currentLeads.length > 0 && (
            <div>
              <UpcomingEventsCarousel
                leads={currentLeads}
                setActiveIndex={setActiveIndex}
                setModalOpen={setModalOpen}
                istableleads={activeTab === "table"}
              />
            </div>
          )}

          <div className='upcoming-events-header-bids'>
            <h3>My bids ({currentBids.length})</h3>
          </div>

          {renderBidCards(currentBids, activeTab === "table", 3)}

          <div className='upcoming-events-view-all'>
            <Link
              to={`/bid-list/${activeIndex}?source=${activeTab}`}
              className="upcoming-events-view-all-link"
            >
              <p>View all</p>
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile View */}
      <div className='upcoming-events-container-mobile'>
        <div className='upcoming-events'>
          <div className='upcoming-events-header'>
            <h3>Upcoming Plan</h3>
            <div className='upcoming-events-header-arrow-up-down'>
              {isOpenBox ?
                <FaChevronDown onClick={() => setIsOpenBox(false)} /> :
                <FaChevronUp onClick={() => setIsOpenBox(true)} />
              }
            </div>
          </div>

          <div className={isOpenBox ? "upcoming-events-header-open" : "upcoming-events-header-close"}>
            {/* Mobile Tabs */}
            <div className='upcoming-events-header-tabs'>
              <div
                className={activeTab === "table" ?
                  "upcoming-events-header-tabs-box-active-tab" :
                  "upcoming-events-header-tabs-box"
                }
                onClick={() => handleTabChange("table")}
                style={{ opacity: tableData.length === 0 ? 0.5 : 1 }}
              >
                Table Booking
              </div>
              <div
                className={activeTab === "package" ?
                  "upcoming-events-header-tabs-box-active-tab" :
                  "upcoming-events-header-tabs-box"
                }
                onClick={() => handleTabChange("package")}
                style={{ opacity: packageData.length === 0 ? 0.5 : 1 }}
              >
                Package Booking
              </div>
            </div>

            <div className='upcoming-events-header-bids'>
              <h3>My bids ({currentBids.length})</h3>
            </div>

            <div className='upcoming-events-header-bids-mobile'>
              {renderBidCards(currentBids, activeTab === "table", 1)}
            </div>

            <div className='upcoming-events-view-all'>
              <Link
                to={`/bid-list/${activeIndex}?source=${activeTab}`}
                className="upcoming-events-view-all-link"
              >
                <p>View all</p>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}